/**
 * 乒乓球游戏通信桥
 *
 * 负责与宿主(pixijs-aibot)之间的 postMessage 双向通信。
 *
 * 上行（游戏 → 宿主）：
 *   ready, scoreUpdate, point_scored, rally_hit, rally_end,
 *   game_over, game_reset, ballPosition, skill_catalog,
 *   combat_state, skill_selected, skill_used
 *
 * 下行（宿主 → 游戏）：
 *   play, pause, reset, botPaddleMove, botUseSkill, setDifficulty
 */

export interface ScoreData {
  p1: number
  p2: number
}

export interface BallPositionData {
  x: number
  y: number
  vx: number
  vy: number
  spin?: number
}

export interface PointScoredData {
  scorer: 'p1' | 'p2'
  p1: number
  p2: number
}

export interface RallyEndData {
  count: number
  scorer: 'p1' | 'p2'
}

export interface GameOverData {
  winner: 'p1' | 'p2'
  winnerLabel: string
  loserLabel: string
  p1: number
  p2: number
  finalScoreText: string
  resultForBot: string
  evaluation: string
  quote: string
  maxRallyCount: number
}

export interface SkillCatalogItem {
  id: string
  name: string
  owner: 'p1' | 'p2'
  keyLabel: string
  energyCost: number
  cooldownMs: number
  maxSpeed: number
  brief: string
  traits: string[]
}

export interface SkillCatalogData {
  playerSkills: SkillCatalogItem[]
  botSkills: SkillCatalogItem[]
  energyRules: string[]
}

export interface CombatStateData {
  reason: 'ready' | 'reset' | 'skill_selected' | 'skill_used' | 'point_scored' | 'hit'
  p1Energy: number
  p2Energy: number
  p1PendingSkill: string | null
  p2PendingSkill: string | null
  activeSkill: string | null
  activeSkillOwner: 'p1' | 'p2' | null
  p1: number
  p2: number
  rallyCount: number
  pressureLevel: number
  p1ReadySkills: string[]
  p2ReadySkills: string[]
}

export interface SkillSelectedData {
  side: 'p1' | 'p2'
  skillId: string | null
  skillName: string | null
  p1Energy: number
  p2Energy: number
}

export interface SkillUsedData {
  side: 'p1' | 'p2'
  skillId: string
  skillName: string
  energyCost: number
  p1Energy: number
  p2Energy: number
  hitZone: 'center' | 'slice' | 'edge'
  charge: number
  p1: number
  p2: number
  rallyCount: number
}

export type HostCommand =
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'reset' }
  | { type: 'botPaddleMove'; y: number }
  | { type: 'botUseSkill'; skillId: string }
  | { type: 'setDifficulty'; level: string }

type CommandHandler = (cmd: HostCommand) => void

export class GameBridge {
  private handlers: CommandHandler[] = []
  private isEmbedded: boolean
  private frameCounter = 0
  private readonly BALL_REPORT_INTERVAL = 4 // 每 4 帧报告一次球位置

  constructor() {
    this.isEmbedded = window.parent !== window

    if (this.isEmbedded) {
      window.addEventListener('message', this.handleMessage.bind(this))
    }
  }

  /** 注册宿主命令回调 */
  onCommand(handler: CommandHandler): void {
    this.handlers.push(handler)
  }

  // ===== 上行消息（游戏 → 宿主）=====

  sendReady(): void {
    this.send({ type: 'ready' })
  }

  sendScoreUpdate(score: ScoreData): void {
    this.send({ type: 'scoreUpdate', ...score })
  }

  sendPointScored(data: PointScoredData): void {
    this.send({ type: 'point_scored', ...data })
  }

  sendRallyHit(): void {
    this.send({ type: 'rally_hit' })
  }

  sendRallyEnd(data: RallyEndData): void {
    this.send({ type: 'rally_end', ...data })
  }

  sendGameOver(data: GameOverData): void {
    this.send({ type: 'game_over', ...data })
  }

  sendGameReset(): void {
    this.send({ type: 'game_reset' })
  }

  sendSkillCatalog(data: SkillCatalogData): void {
    this.send({ type: 'skill_catalog', ...data })
  }

  sendCombatState(data: CombatStateData): void {
    this.send({ type: 'combat_state', ...data })
  }

  sendSkillSelected(data: SkillSelectedData): void {
    this.send({ type: 'skill_selected', ...data })
  }

  sendSkillUsed(data: SkillUsedData): void {
    this.send({ type: 'skill_used', ...data })
  }

  /**
   * 报告球位置（带帧率限制，每 4 帧发送一次）
   * 调用方每帧调用，内部自动节流
   */
  sendBallPosition(data: BallPositionData): void {
    this.frameCounter++
    if (this.frameCounter % this.BALL_REPORT_INTERVAL !== 0) return
    this.send({ type: 'ballPosition', ...data })
  }

  // ===== 内部 =====

  private handleMessage(event: MessageEvent): void {
    const data = event.data
    if (!data || typeof data.type !== 'string') return

    for (const handler of this.handlers) {
      handler(data as HostCommand)
    }
  }

  private send(data: Record<string, unknown>): void {
    if (this.isEmbedded) {
      window.parent.postMessage(data, '*')
    }
  }
}
